import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Plus, Store, X } from "lucide-react";
import { Button } from "@wedding/ui";
import type { EntityRecord, EntityType } from "@wedding/schemas";
import { normalizeGuestSide, vendorTypes } from "../config";
import type { ModuleConfig } from "../config";

function toFormValues(config: ModuleConfig, data: Record<string, unknown>) {
  const values = { ...data } as Record<string, unknown>;
  if (config.type === "guests" && values.side)
    values.side = normalizeGuestSide(values.side);
  for (const field of config.fields) {
    if (field.type === "currency" && typeof values[field.key] === "number") {
      values[field.key] = Number(values[field.key]) / 100;
    }
  }
  return values;
}

function toStorageValues(config: ModuleConfig, data: Record<string, any>) {
  const values = { ...data } as Record<string, unknown>;
  for (const field of config.fields) {
    if (field.type !== "currency") continue;
    const amount = Number(values[field.key]);
    if (Number.isFinite(amount)) values[field.key] = Math.round(amount * 100);
    else delete values[field.key];
  }
  return values;
}

const emptyVendor = {
  vendorType: "待选择",
  vendorName: "",
  contact: "",
  phone: "",
  wechat: "",
};

type Props = {
  config: ModuleConfig;
  entity?: EntityRecord | undefined;
  open: boolean;
  saving: boolean;
  relationOptions: Partial<Record<EntityType, EntityRecord[]>>;
  onClose(): void;
  onSave(data: Record<string, unknown>): void;
  onCreateRelation?: (
    type: EntityType,
    data: Record<string, unknown>,
  ) => Promise<EntityRecord>;
};

export function EntityEditor({
  config,
  entity,
  open,
  saving,
  relationOptions,
  onClose,
  onSave,
  onCreateRelation,
}: Props) {
  const { register, handleSubmit, reset, setValue, watch } = useForm<
    Record<string, any>
  >({
    defaultValues: toFormValues(config, entity?.data ?? {}),
  });
  const [quickField, setQuickField] = useState<string>();
  const [vendorDraft, setVendorDraft] = useState(emptyVendor);
  const [creatingRelation, setCreatingRelation] = useState(false);
  const [relationError, setRelationError] = useState("");
  const [relationSearch, setRelationSearch] = useState<Record<string, string>>(
    {},
  );
  const [relationFilters, setRelationFilters] = useState<
    Record<string, { finalOnly: boolean; confirmedOnly: boolean }>
  >({});
  const [recentRelations, setRecentRelations] = useState<string[]>(() => {
    try {
      return JSON.parse(
        localStorage.getItem("wedding-recent-vendors") ?? "[]",
      ) as string[];
    } catch {
      return [];
    }
  });
  const dialogRef = useRef<HTMLElement>(null);
  const [createdRelations, setCreatedRelations] = useState<
    Partial<Record<EntityType, EntityRecord[]>>
  >({});
  const [pendingRelation, setPendingRelation] = useState<{
    fieldKey: string;
    relationType: EntityType;
    id: string;
  }>();

  useEffect(() => {
    reset(toFormValues(config, entity?.data ?? {}));
    setQuickField(undefined);
    setVendorDraft(emptyVendor);
    setRelationError("");
    setRelationSearch({});
    setRelationFilters({});
    setCreatedRelations({});
    setPendingRelation(undefined);
  }, [config, entity, reset, open]);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => {
      dialogRef.current
        ?.querySelector<HTMLElement>(
          "input:not([type='hidden']), select, textarea, button",
        )
        ?.focus();
    });
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !creatingRelation && !saving) onClose();
      if (event.key === "Tab" && dialogRef.current) {
        const focusable = [
          ...dialogRef.current.querySelectorAll<HTMLElement>(
            "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])",
          ),
        ];
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", closeOnEscape);
      previousFocus?.focus();
    };
  }, [creatingRelation, onClose, open, saving]);

  const options = useMemo(() => {
    const result: Partial<Record<EntityType, EntityRecord[]>> = {};
    for (const type of Object.keys(relationOptions) as EntityType[]) {
      result[type] = [
        ...(createdRelations[type] ?? []),
        ...(relationOptions[type] ?? []).filter(
          (item) =>
            !(createdRelations[type] ?? []).some(
              (created) => created.id === item.id,
            ),
        ),
      ];
    }
    for (const type of Object.keys(createdRelations) as EntityType[]) {
      const created = createdRelations[type];
      if (!result[type] && created) result[type] = created;
    }
    return result;
  }, [createdRelations, relationOptions]);

  useEffect(() => {
    if (!pendingRelation) return;
    const available = options[pendingRelation.relationType]?.some(
      (item) => item.id === pendingRelation.id,
    );
    if (!available) return;
    setValue(pendingRelation.fieldKey, pendingRelation.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setPendingRelation(undefined);
  }, [options, pendingRelation, setValue]);

  if (!open) return null;

  const createVendor = async (fieldKey: string) => {
    if (!onCreateRelation) return;
    if (!vendorDraft.vendorName.trim()) {
      setRelationError("请填写供应商名称");
      return;
    }
    setCreatingRelation(true);
    setRelationError("");
    try {
      const created = await onCreateRelation("vendors", vendorDraft);
      setCreatedRelations((current) => ({
        ...current,
        vendors: [created, ...(current.vendors ?? [])],
      }));
      setPendingRelation({
        fieldKey,
        relationType: "vendors",
        id: created.id,
      });
      rememberRelation(created.id);
      setQuickField(undefined);
      setVendorDraft(emptyVendor);
    } catch (error) {
      setRelationError(
        error instanceof Error ? error.message : "供应商创建失败，请重试",
      );
    } finally {
      setCreatingRelation(false);
    }
  };

  const rememberRelation = (id: string) => {
    if (!id) return;
    setRecentRelations((current) => {
      const next = [id, ...current.filter((item) => item !== id)].slice(0, 8);
      localStorage.setItem("wedding-recent-vendors", JSON.stringify(next));
      return next;
    });
  };

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !saving &&
          !creatingRelation
        )
          onClose();
      }}
    >
      <aside
        ref={dialogRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-title"
      >
        <header>
          <div>
            <span className="eyebrow">{entity ? "编辑记录" : "新增记录"}</span>
            <h2 id="editor-title">{config.singular}</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="关闭"
          >
            <X size={20} />
          </button>
        </header>
        {Boolean(entity?.data.isDerived) && (
          <div className="notice">
            此任务由「{String(entity?.data.sourceModule)}
            」自动生成。任务名称和来源信息请在对应业务模块修改。
          </div>
        )}
        <form
          onSubmit={handleSubmit((data) =>
            onSave(toStorageValues(config, data)),
          )}
        >
          <div className="form-grid">
            {config.fields.map((field) => {
              if (
                config.type === "guests" &&
                field.key === "mailingAddress" &&
                watch("invitationDelivery") !== "邮寄"
              )
                return null;
              const readOnly =
                Boolean(field.readOnly) ||
                (Boolean(entity?.data.isDerived) &&
                  ["title", "category"].includes(field.key));
              const label = (
                <span>
                  {field.label}
                  {field.required && <b>*</b>}
                </span>
              );

              if (field.type === "relation") {
                const relationType = field.relationType;
                const canQuickCreate =
                  relationType === "vendors" && Boolean(onCreateRelation);
                const inputId = `entity-field-${field.key}`;
                const filter = relationFilters[field.key] ?? {
                  finalOnly: false,
                  confirmedOnly: false,
                };
                const selectedId = String(watch(field.key) ?? "");
                const filteredOptions = (
                  relationType ? (options[relationType] ?? []) : []
                )
                  .filter((option) => {
                    if (option.id === selectedId) return true;
                    const query = (relationSearch[field.key] ?? "")
                      .trim()
                      .toLowerCase();
                    const searchableKeys =
                      relationType === "vendors"
                        ? ["vendorName", "vendorType", "contact", "phone"]
                        : (field.relationLabelKeys ?? ["name"]);
                    const matchesQuery =
                      !query ||
                      searchableKeys.some((key) =>
                        String(option.data[key] ?? "")
                          .toLowerCase()
                          .includes(query),
                      );
                    return (
                      matchesQuery &&
                      (!filter.finalOnly ||
                        Boolean(option.data.finalSelected)) &&
                      (!filter.confirmedOnly ||
                        Boolean(option.data.availabilityConfirmed))
                    );
                  })
                  .sort((left, right) => {
                    const leftRecent = recentRelations.indexOf(left.id);
                    const rightRecent = recentRelations.indexOf(right.id);
                    if (leftRecent >= 0 || rightRecent >= 0)
                      return (
                        (leftRecent < 0 ? 99 : leftRecent) -
                        (rightRecent < 0 ? 99 : rightRecent)
                      );
                    return String(
                      left.data.vendorName ?? left.data.name ?? "",
                    ).localeCompare(
                      String(right.data.vendorName ?? right.data.name ?? ""),
                      "zh-CN",
                    );
                  });
                return (
                  <div
                    className="relation-field relation-combobox span-2"
                    key={field.key}
                    role="group"
                    aria-label={`${field.label}选择器`}
                  >
                    <label htmlFor={inputId}>{label}</label>
                    {(options[relationType ?? "vendors"]?.length ?? 0) > 8 && (
                      <input
                        className="relation-search"
                        type="search"
                        value={relationSearch[field.key] ?? ""}
                        onChange={(event) =>
                          setRelationSearch((current) => ({
                            ...current,
                            [field.key]: event.target.value,
                          }))
                        }
                        placeholder={`搜索${field.label}名称、类型、联系人或电话`}
                        aria-label={`搜索${field.label}`}
                      />
                    )}
                    {relationType === "vendors" &&
                      (options.vendors?.length ?? 0) > 8 && (
                        <div className="relation-filter-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={filter.finalOnly}
                              onChange={(event) =>
                                setRelationFilters((current) => ({
                                  ...current,
                                  [field.key]: {
                                    ...filter,
                                    finalOnly: event.target.checked,
                                  },
                                }))
                              }
                            />{" "}
                            仅看最终选择
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={filter.confirmedOnly}
                              onChange={(event) =>
                                setRelationFilters((current) => ({
                                  ...current,
                                  [field.key]: {
                                    ...filter,
                                    confirmedOnly: event.target.checked,
                                  },
                                }))
                              }
                            />{" "}
                            仅看档期已确认
                          </label>
                          <span>{filteredOptions.length} 个可选</span>
                        </div>
                      )}
                    <div className="relation-picker">
                      <select
                        id={inputId}
                        {...register(field.key, {
                          ...(field.required ? { required: true } : {}),
                          onChange: (event) =>
                            rememberRelation(String(event.target.value)),
                        })}
                        disabled={readOnly}
                      >
                        <option value="">请选择{field.label}</option>
                        {filteredOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {recentRelations.includes(option.id)
                              ? "最近使用 · "
                              : ""}
                            {(field.relationLabelKeys ?? ["name"])
                              .map((key) => String(option.data[key] ?? ""))
                              .filter(Boolean)
                              .join(" · ") || option.id}
                          </option>
                        ))}
                      </select>
                      {canQuickCreate && !readOnly && (
                        <button
                          type="button"
                          className="quick-create-button"
                          onClick={() => {
                            setQuickField(
                              quickField === field.key ? undefined : field.key,
                            );
                            setRelationError("");
                          }}
                          aria-expanded={quickField === field.key}
                        >
                          {quickField === field.key ? (
                            <X size={15} />
                          ) : (
                            <Plus size={15} />
                          )}
                          {quickField === field.key ? "收起" : "新建供应商"}
                        </button>
                      )}
                    </div>
                    {quickField === field.key && (
                      <section
                        className="quick-relation-card"
                        aria-label="快速新建供应商"
                      >
                        <header>
                          <Store size={18} />
                          <div>
                            <strong>新建供应商并自动选中</strong>
                            <p>
                              保存后会加入供应商库，可在其他预算、待办和清单中继续使用。
                            </p>
                          </div>
                        </header>
                        <div className="form-grid">
                          <label>
                            <span>供应商类型*</span>
                            <select
                              required
                              value={vendorDraft.vendorType}
                              onChange={(event) =>
                                setVendorDraft({
                                  ...vendorDraft,
                                  vendorType: event.target.value,
                                })
                              }
                            >
                              {vendorTypes.map((type) => (
                                <option key={type}>{type}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>供应商名称*</span>
                            <input
                              required
                              autoFocus
                              value={vendorDraft.vendorName}
                              onChange={(event) =>
                                setVendorDraft({
                                  ...vendorDraft,
                                  vendorName: event.target.value,
                                })
                              }
                            />
                          </label>
                          <label>
                            <span>联系人</span>
                            <input
                              value={vendorDraft.contact}
                              onChange={(event) =>
                                setVendorDraft({
                                  ...vendorDraft,
                                  contact: event.target.value,
                                })
                              }
                            />
                          </label>
                          <label>
                            <span>电话</span>
                            <input
                              type="tel"
                              value={vendorDraft.phone}
                              onChange={(event) =>
                                setVendorDraft({
                                  ...vendorDraft,
                                  phone: event.target.value,
                                })
                              }
                            />
                          </label>
                        </div>
                        {relationError && (
                          <p className="form-error">{relationError}</p>
                        )}
                        <footer>
                          <button
                            type="button"
                            className="secondary-action"
                            onClick={() => setQuickField(undefined)}
                          >
                            取消
                          </button>
                          <Button
                            type="button"
                            disabled={creatingRelation}
                            onClick={() => void createVendor(field.key)}
                          >
                            {creatingRelation ? "正在创建…" : "创建并使用"}
                          </Button>
                        </footer>
                      </section>
                    )}
                  </div>
                );
              }

              if (field.type === "checkbox") {
                return (
                  <label key={field.key} className="checkbox-field">
                    <input
                      className="checkbox"
                      type="checkbox"
                      {...register(field.key)}
                      disabled={readOnly}
                    />
                    {label}
                  </label>
                );
              }

              return (
                <label
                  key={field.key}
                  className={field.type === "textarea" ? "span-2" : ""}
                >
                  {label}
                  {field.type === "textarea" ? (
                    <textarea
                      {...register(
                        field.key,
                        field.required ? { required: true } : {},
                      )}
                      disabled={readOnly}
                      rows={4}
                    />
                  ) : field.type === "select" ? (
                    <select
                      {...register(
                        field.key,
                        field.required ? { required: true } : {},
                      )}
                      disabled={readOnly}
                    >
                      <option value="">请选择</option>
                      {field.options?.map((option) => (
                        <option key={option} value={option}>
                          {field.optionLabels?.[option] ?? option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={
                        field.type === "currency"
                          ? "number"
                          : (field.type ?? "text")
                      }
                      step={field.type === "currency" ? "0.01" : undefined}
                      min={field.type === "currency" ? "0" : undefined}
                      inputMode={
                        field.type === "currency" ? "decimal" : undefined
                      }
                      placeholder={
                        field.type === "currency"
                          ? "请输入人民币金额"
                          : undefined
                      }
                      {...register(field.key, {
                        ...(field.required ? { required: true } : {}),
                        ...(["number", "currency"].includes(field.type ?? "")
                          ? { valueAsNumber: true }
                          : {}),
                      })}
                      disabled={readOnly}
                    />
                  )}
                </label>
              );
            })}
          </div>
          <footer>
            <Button type="button" className="secondary" onClick={onClose}>
              取消
            </Button>
            <Button type="submit" disabled={saving || creatingRelation}>
              {saving
                ? "保存中…"
                : entity
                  ? "保存更改"
                  : `创建${config.singular}`}
            </Button>
          </footer>
        </form>
      </aside>
    </div>
  );
}
